<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';
import type { PageFlip } from 'page-flip';

export interface AlbumLeaf {
  key: string;
  side: 'left' | 'right';
  slots: (TMissionOrBadge | null)[];
}

const props = defineProps<{
  leaves: AlbumLeaf[];
  /** Folha exibida logo após a inicialização (preserva a página entre remounts). */
  initialIndex?: number;
}>();

const emit = defineEmits<{
  (e: 'flip', index: number): void;
  (e: 'open-detail', badge: TMissionOrBadge): void;
}>();

const root = ref<HTMLElement | null>(null);

let Ctor: typeof PageFlip | null = null;
let pageFlip: PageFlip | null = null;
let lastWidth = 0;
let resizeTimer: ReturnType<typeof setTimeout> | null = null;

// Dimensiona a folha pela LARGURA REAL medida (não por proporção fixa): largura =
// metade do container; altura = altura real do conteúdo (header + fileiras + dock).
// Assim encaixa em qualquer resolução — sem sobra vertical em telas largas nem o
// dock invadindo as figurinhas em telas médias. Espelha as métricas do grid CSS.
function computeDims() {
  const total = root.value?.clientWidth ?? 0;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const leafW = Math.max(140, Math.floor(total / 2));
  const half = props.leaves[0]?.slots.length ?? 8;

  const cols = vw < 600 ? 1 : vw < 768 ? 2 : 4;
  const rows = Math.max(1, Math.ceil(half / cols));
  const gap = vw < 600 ? 20 : vw >= 768 && vw <= 1023 ? 14 : 32;

  let stickerH: number;

  if (vw < 768) {
    stickerH = 144.329;
  } else {
    const stickerW = Math.min(108, (leafW - 28 - (cols - 1) * gap) / cols);

    stickerH = (stickerW * 4) / 3;
  }

  const topPad = vw < 768 ? 48 : 56;
  const botPad = vw < 768 ? 54 : 60;
  // +8 de folga pra evitar corte por arredondamento.
  const leafH = Math.ceil(
    topPad + rows * stickerH + (rows - 1) * gap + botPad + 8,
  );

  return { leafW, leafH };
}

async function build() {
  if (!root.value || !props.leaves.length) return;

  // StPageFlip mexe no DOM (envolve as `.page` em wrappers próprios), por isso
  // só roda no client e o componente é remontado via `:key` quando a estrutura
  // de folhas muda — assim o Vue nunca tenta repatch nós já movidos pela lib.
  if (!Ctor) ({ PageFlip: Ctor } = await import('page-flip'));

  const prev = pageFlip
    ? pageFlip.getCurrentPageIndex()
    : (props.initialIndex ?? 0);

  if (pageFlip) {
    pageFlip.destroy();
    pageFlip = null;
  }

  const { leafW, leafH } = computeDims();

  lastWidth = leafW;

  pageFlip = new Ctor(root.value, {
    width: leafW,
    height: leafH,
    size: 'fixed',
    drawShadow: true,
    flippingTime: 700,
    maxShadowOpacity: 0.5,
    showCover: false,
    usePortrait: false,
    // true: arraste VERTICAL rola a página; só arraste HORIZONTAL vira a folha.
    mobileScrollSupport: true,
    swipeDistance: 30,
    showPageCorners: true,
    // Vira só pelo arraste das bordas/setas, não pelo clique na figurinha.
    disableFlipByClick: true,
  });

  pageFlip.loadFromHTML(root.value.querySelectorAll('.flip-page'));
  pageFlip.on('flip', (e: { data: number }) => emit('flip', e.data));

  const start = Math.min(Math.max(0, prev), props.leaves.length - 1);

  if (start > 0) pageFlip.turnToPage(start);
}

// Recalcula (re-inicializa) quando a largura muda de forma relevante.
function onResize() {
  if (resizeTimer) clearTimeout(resizeTimer);

  resizeTimer = setTimeout(() => {
    const leafW = Math.floor((root.value?.clientWidth ?? 0) / 2);

    if (Math.abs(leafW - lastWidth) > 8) build();
  }, 150);
}

function flipNext() {
  pageFlip?.flipNext();
}

function flipPrev() {
  pageFlip?.flipPrev();
}

function flipTo(index: number) {
  pageFlip?.flip(index);
}

defineExpose({ flipNext, flipPrev, flipTo });

onMounted(() => {
  build();
  window.addEventListener('resize', onResize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize);

  if (resizeTimer) clearTimeout(resizeTimer);

  pageFlip?.destroy();
  pageFlip = null;
});
</script>

<template>
  <div
    ref="root"
    class="album-flipbook"
  >
    <div
      v-for="leaf in leaves"
      :key="leaf.key"
      class="flip-page"
      :class="`flip-page--${leaf.side}`"
    >
      <!-- "FIFA" decorativo atrás das figurinhas. Cada folha mostra metade da
           palavra (alinhada à lombada) pra reconstruir "FIFA" no centro do spread. -->
      <div
        aria-hidden="true"
        class="flip-page__fifa"
        ><span>FIFA</span></div
      >

      <div class="flip-page__grid">
        <template
          v-for="(slot, idx) in leaf.slots"
          :key="idx"
        >
          <PerfilOficialAlbumStickerSlot
            v-if="slot"
            :badge="slot"
            @open-detail="emit('open-detail', $event)"
          />
          <div
            v-else
            class="sticker-slot sticker-slot--empty"
          ></div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.album-flipbook {
  width: 100%;
  margin: 0 auto;
}

/* Cada folha = uma meia-página do álbum. Reproduz EXATAMENTE o background do
   `.album-wrapper` (mesmas camadas), mas com `background-size: 200%`: a folha
   esquerda mostra a metade esquerda e a direita a metade direita, reconstruindo
   o painel inteiro de forma contínua. O `linear-gradient(126deg)` é opaco, então
   a página não fica transparente durante a virada. */
.flip-page {
  background-image:
    radial-gradient(
      121.1% 88.65% at 50% 0%,
      rgba(41, 41, 255, 0.89) 0%,
      rgba(41, 41, 255, 0) 55%
    ),
    linear-gradient(
      90deg,
      rgba(0, 0, 0, 0.85) 0%,
      rgba(0, 0, 0, 0) 8%,
      rgba(0, 0, 0, 0) 92%,
      rgba(0, 0, 0, 0.85) 100%
    ),
    linear-gradient(126deg, #0f0f4f 0%, #030312 100%);
  background-repeat: no-repeat;
  background-size: 200% 100%;
  overflow: hidden;
  /* Promove a folha a uma camada de GPU: o fundo (gradientes) é rasterizado uma
     vez e a virada só transforma a camada — repaint bem mais barato, sem travar. */
  transform: translateZ(0);
  backface-visibility: hidden;
  will-change: transform;
}

/* Sombra interna na borda da lombada de cada folha → some na borda externa,
   escurece no centro: junta as duas e forma a sombra do vinco do livro. */
.flip-page--left {
  background-position: left center;
  box-shadow: inset -36px 0 55px -28px rgba(0, 0, 0, 0.9);
}

.flip-page--right {
  background-position: right center;
  box-shadow: inset 36px 0 55px -28px rgba(0, 0, 0, 0.9);
}

/* Metade da palavra "FIFA" por folha, centrada na lombada (mesma fonte/tom do
   `.fifa-bg` do wrapper). Fica atrás do grid via z-index. */
.flip-page__fifa {
  position: absolute;
  inset: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  z-index: 0;
  pointer-events: none;
}

.flip-page__fifa span {
  position: absolute;
  width: 200%;
  text-align: center;
  font-family: 'Roboto', 'Inter', sans-serif;
  font-weight: 900;
  font-size: clamp(66px, 12.1vw, 176px);
  letter-spacing: 0.1em;
  color: rgba(255, 255, 255, 0.12);
  white-space: nowrap;
}

/* width:200% + left/right:0 → o centro da palavra cai na borda da lombada. */
.flip-page--left .flip-page__fifa span {
  left: 0;
}

.flip-page--right .flip-page__fifa span {
  right: 0;
}

.flip-page__grid {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  display: grid;
  /* Colunas fixas (108px) agrupadas na lombada (justify-content), como o álbum
     original: gaps justos e uniformes. O padding da lombada = metade do gap
     (16 = 32/2) → o vão central bate com o gap entre colunas. */
  grid-template-columns: repeat(4, minmax(0, 108px));
  align-content: center;
  gap: 32px;
  /* topo livre pro header (absolute) e base livre pro dock (absolute). */
  padding: 56px 12px 60px;
  box-sizing: border-box;
}

.flip-page--left .flip-page__grid {
  justify-content: end;
  padding-right: 16px;
}

.flip-page--right .flip-page__grid {
  justify-content: start;
  padding-left: 16px;
}

.flip-page__grid > * {
  width: 100%;
  max-width: 108px;
  aspect-ratio: 3 / 4;
}

.sticker-slot--empty {
  visibility: hidden;
  pointer-events: none;
}

@media (max-width: 767px) {
  .flip-page__fifa span {
    font-size: clamp(100px, 23vw, 251px);
  }

  .flip-page__grid {
    padding-top: 48px;
    padding-bottom: 54px;
  }

  .flip-page--left .flip-page__grid {
    padding-right: 10px;
  }

  .flip-page--right .flip-page__grid {
    padding-left: 10px;
  }
}

@media (max-width: 599px) {
  .flip-page__grid {
    grid-template-columns: 106.213px;
    gap: 20px;
  }

  .flip-page__grid > * {
    width: 106.213px;
    height: 144.329px;
    max-width: none;
    aspect-ratio: auto;
  }
}

@media (min-width: 600px) and (max-width: 767px) {
  .flip-page__grid {
    grid-template-columns: repeat(2, 106.213px);
  }

  .flip-page__grid > * {
    width: 106.213px;
    height: 144.329px;
    max-width: none;
    aspect-ratio: auto;
  }
}

@media (min-width: 768px) and (max-width: 1023px) {
  .flip-page__grid {
    gap: 14px;
  }

  .flip-page--left .flip-page__grid {
    padding-right: 7px;
  }

  .flip-page--right .flip-page__grid {
    padding-left: 7px;
  }
}

@media (min-width: 768px) and (max-width: 1023px) {
  .flip-page__grid {
    gap: 14px;
  }

  .flip-page--left .flip-page__grid {
    padding-right: 7px;
  }

  .flip-page--right .flip-page__grid {
    padding-left: 7px;
  }
}
</style>
